export interface CoalescingBuffer<TKey, TInput, TOutput> {
    push: (key: TKey, value: TInput) => Promise<TOutput>;
}

interface PendingEntry<TInput, TOutput> {
    latest: TInput;
    timer: ReturnType<typeof setTimeout>;
    resolvers: ((result: TOutput) => void)[];
    rejecters: ((error: unknown) => void)[];
}

export function createCoalescingBuffer<TKey, TInput, TOutput>(
    flush: (latest: TInput) => Promise<TOutput>,
    delayMs: number
): CoalescingBuffer<TKey, TInput, TOutput> {
    const pending = new Map<TKey, PendingEntry<TInput, TOutput>>();

    const flushKey = (key: TKey): void => {
        const entry = pending.get(key);
        if (!entry) {
            return;
        }
        pending.delete(key);

        flush(entry.latest)
            .then((result) => entry.resolvers.forEach((resolve) => resolve(result)))
            .catch((error) => entry.rejecters.forEach((reject) => reject(error)));
    };

    return {
        push: (key: TKey, value: TInput): Promise<TOutput> => new Promise((resolve, reject) => {
            const entry = pending.get(key);

            if (entry) {
                clearTimeout(entry.timer);
                entry.latest = value;
                entry.resolvers.push(resolve);
                entry.rejecters.push(reject);
                entry.timer = setTimeout(() => flushKey(key), delayMs);
            } else {
                pending.set(key, {
                    latest: value,
                    resolvers: [resolve],
                    rejecters: [reject],
                    timer: setTimeout(() => flushKey(key), delayMs)
                });
            }
        })
    };
}
